<script setup lang="ts">
import { onMounted, ref, shallowRef } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ArrowRight, Eye, EyeOff } from "lucide-vue-next";
import AuthPageShell from "@/components/auth/AuthPageShell.vue";
import ExternalMfaChallengeForm from "@/components/auth/ExternalMfaChallenge.vue";
import ExternalTotpEnrollment from "@/components/auth/ExternalTotpEnrollment.vue";
import LogoMark from "@/components/LogoMark.vue";
import {
  finishExternalTotpEnrollment,
  requestPasswordReset,
  signInExternal,
  signOutUser,
  startExternalTotpEnrollment,
  type ExternalMfaChallenge,
  type ExternalTotpEnrollment as ExternalTotpEnrollmentState,
  useEmulators,
} from "@/services/firebase";
import { useWorkspaceStore } from "@/stores/workspace";
import { externalMfaErrorMessage } from "@/utils/auth-errors";

const workspace = useWorkspaceStore();
const router = useRouter();
const route = useRoute();
const email = ref("");
const password = ref("");
const showPassword = ref(false);
const resetOpen = ref(false);
const resetSent = ref(false);
const resetBusy = ref(false);
const mfaChallenge = ref<ExternalMfaChallenge | null>(null);
const mfaEnrollment = shallowRef<ExternalTotpEnrollmentState | null>(null);
const enrollmentBusy = ref(false);
const mfaError = ref("");
const demoProfiles = [
  {
    kind: "admin" as const,
    initials: "AM",
    name: "Alex Morgan",
    detail: "VIPdesk workspace administrator",
  },
  {
    kind: "internal" as const,
    initials: "KN",
    name: "Kim Nacas",
    detail: "VIPdesk project owner · sees Internal and Shared work",
  },
  {
    kind: "client" as const,
    initials: "PS",
    name: "Priya Shah",
    detail: "Thorne client lead · sees Shared work only",
  },
];

onMounted(() => {
  if (workspace.user) router.replace("/projects");
});

function destination() {
  return typeof route.query.redirect === "string"
    ? route.query.redirect
    : "/projects";
}

async function login(kind: "internal" | "admin" | "client" | "microsoft") {
  if (kind !== "microsoft" && useEmulators)
    sessionStorage.setItem("planner-demo-profile", kind);
  await workspace.login(kind);
  if (workspace.user) router.replace(destination());
}

async function loginExternal() {
  mfaError.value = "";
  const result = await workspace.loginExternal(email.value, password.value);
  if (result?.status === "mfa-required") {
    mfaChallenge.value = result.challenge;
    mfaError.value = "";
    return;
  }
  if (result?.status === "mfa-enrollment-required") {
    enrollmentBusy.value = true;
    try {
      mfaEnrollment.value = await startExternalTotpEnrollment(password.value);
    } catch (error) {
      mfaError.value = externalMfaErrorMessage(error, "enroll");
      await signOutUser().catch(() => undefined);
    } finally {
      enrollmentBusy.value = false;
    }
    return;
  }
  if (workspace.user) router.replace(destination());
}

async function completeEnrollment(code: string) {
  if (!mfaEnrollment.value) return;
  enrollmentBusy.value = true;
  mfaError.value = "";
  try {
    await finishExternalTotpEnrollment(mfaEnrollment.value, code);
    mfaEnrollment.value = null;
    await signOutUser();
    const signIn = await signInExternal(email.value, password.value);
    if (signIn.status !== "mfa-required") {
      throw new Error("planner/mfa-challenge-required");
    }
    mfaChallenge.value = signIn.challenge;
  } catch (error) {
    mfaError.value = externalMfaErrorMessage(error, "enroll");
  } finally {
    enrollmentBusy.value = false;
  }
}

async function verifyExternalMfa(value: { factorUid: string; code: string }) {
  if (!mfaChallenge.value) return;
  mfaError.value = "";
  try {
    await workspace.completeExternalMfa(
      mfaChallenge.value,
      value.factorUid,
      value.code,
    );
    if (workspace.user) await router.replace(destination());
  } catch (error) {
    mfaError.value = externalMfaErrorMessage(error, "sign-in");
  }
}

async function cancelExternalMfa() {
  mfaChallenge.value = null;
  mfaEnrollment.value = null;
  mfaError.value = "";
  password.value = "";
  await signOutUser().catch(() => undefined);
}

async function sendReset() {
  if (!email.value.trim()) return;
  resetBusy.value = true;
  try {
    await requestPasswordReset(email.value);
  } catch {
    // Always show the same result so this form cannot reveal registered emails.
  } finally {
    resetBusy.value = false;
    resetSent.value = true;
  }
}
</script>

<template>
  <AuthPageShell labelled-by="login-title">
    <div class="login-content">
      <div class="login-heading">
        <LogoMark class="login-mark" tone="dark" />
        <h2 id="login-title">Welcome to Project Planner</h2>
        <p>Sign in to continue to your workspace.</p>
      </div>

      <div class="login-actions">
        <button
          class="ms-button"
          type="button"
          :disabled="workspace.loading"
          @click="login('microsoft')"
        >
          <span class="ms-grid" aria-hidden="true"
            ><i></i><i></i><i></i><i></i
          ></span>
          Continue with Microsoft
          <ArrowRight :size="17" aria-hidden="true" />
        </button>

        <div class="login-divider"><span>Client or external account</span></div>

        <ExternalMfaChallengeForm
          v-if="mfaChallenge"
          :challenge="mfaChallenge"
          :busy="workspace.loading"
          :error="mfaError"
          @verify="verifyExternalMfa"
          @cancel="cancelExternalMfa"
        />

        <ExternalTotpEnrollment
          v-else-if="mfaEnrollment"
          :enrollment="mfaEnrollment"
          :busy="enrollmentBusy"
          :error="mfaError"
          @verify="completeEnrollment"
          @cancel="cancelExternalMfa"
        />

        <form
          v-else-if="!resetOpen"
          class="credential-form"
          @submit.prevent="loginExternal"
        >
          <p
            v-if="route.query.mfa === 'required'"
            class="login-notice"
            role="status"
          >
            Sign in again to finish setting up or verifying your authenticator
            app.
          </p>
          <p v-if="mfaError" class="login-error" role="alert">
            {{ mfaError }}
          </p>
          <div class="field">
            <label for="external-email">Email</label>
            <input
              id="external-email"
              v-model="email"
              class="input"
              type="email"
              autocomplete="username"
              required
            />
          </div>
          <div class="field">
            <label for="external-password">Password</label>
            <div class="password-field">
              <input
                id="external-password"
                v-model="password"
                class="input"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                required
              />
              <button
                class="password-toggle"
                type="button"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                :aria-pressed="showPassword"
                @click="showPassword = !showPassword"
              >
                <EyeOff v-if="showPassword" :size="17" />
                <Eye v-else :size="17" />
              </button>
            </div>
          </div>
          <button
            class="button button--primary login-submit"
            type="submit"
            :disabled="workspace.loading || !email.trim() || !password"
          >
            {{ workspace.loading ? "Signing in…" : "Sign in with email" }}
          </button>
          <button
            class="text-action forgot-action"
            type="button"
            @click="
              resetOpen = true;
              resetSent = false;
            "
          >
            Forgot password?
          </button>
        </form>

        <form v-else class="credential-form" @submit.prevent="sendReset">
          <div>
            <h3>Reset your password</h3>
            <p class="reset-copy">
              Enter your account email. If it is eligible, we’ll send password
              reset instructions.
            </p>
          </div>
          <div class="field">
            <label for="reset-email">Email</label>
            <input
              id="reset-email"
              v-model="email"
              class="input"
              type="email"
              autocomplete="username"
              required
            />
          </div>
          <p v-if="resetSent" class="reset-result" role="status">
            If an eligible account matches that email, reset instructions are on
            their way.
          </p>
          <button
            class="button button--primary login-submit"
            type="submit"
            :disabled="resetBusy || !email.trim()"
          >
            {{ resetBusy ? "Sending…" : "Send reset instructions" }}
          </button>
          <button
            class="text-action forgot-action"
            type="button"
            @click="resetOpen = false"
          >
            Back to sign in
          </button>
        </form>

        <template v-if="useEmulators && !mfaChallenge && !mfaEnrollment">
          <div class="login-divider"><span>Local demo profiles</span></div>
          <button
            v-for="profile in demoProfiles"
            :key="profile.kind"
            class="demo-profile"
            type="button"
            :disabled="workspace.loading"
            @click="login(profile.kind)"
          >
            <span
              class="avatar"
              :class="profile.kind !== 'client' && 'avatar--brand'"
            >
              {{ profile.initials }}
            </span>
            <span
              ><strong>{{ profile.name }}</strong
              ><small>{{ profile.detail }}</small></span
            >
            <ArrowRight :size="17" aria-hidden="true" />
          </button>
        </template>
      </div>

      <div v-if="workspace.error" class="login-error" role="alert">
        {{ workspace.error }}
      </div>
      <p class="login-help">
        VIPdesk employees can sign in with Microsoft. Client and external
        participants need an invitation before signing in with email.
      </p>
    </div>
  </AuthPageShell>
</template>

<style scoped>
.login-content {
  width: min(420px, 100%);
  display: grid;
  gap: 28px;
}
.login-heading {
  text-align: center;
}
.login-mark {
  margin: 0 auto 20px;
}
.login-mark :deep(.logo-mark__wordmark) {
  width: 132px;
}
.login-heading h2 {
  font-size: 25px;
}
.login-heading p {
  margin-top: 7px;
  color: var(--text-soft);
}
.login-actions,
.credential-form {
  display: grid;
  gap: 12px;
}
.credential-form .input {
  min-height: 44px;
}
.ms-button,
.demo-profile {
  width: 100%;
  min-height: 52px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 13px;
  border: 1px solid var(--rule-strong);
  border-radius: 7px;
  background: white;
  cursor: pointer;
  text-align: left;
}
.ms-button {
  justify-content: center;
  font-weight: 700;
}
.ms-button:hover,
.demo-profile:hover {
  border-color: #899d98;
  background: var(--sheet-subtle);
}
.ms-button:disabled,
.demo-profile:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.ms-button svg,
.demo-profile > svg {
  margin-left: auto;
  color: var(--text-faint);
}
.ms-grid {
  width: 18px;
  height: 18px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px;
}
.ms-grid i:nth-child(1) {
  background: #f35325;
}
.ms-grid i:nth-child(2) {
  background: #81bc06;
}
.ms-grid i:nth-child(3) {
  background: #05a6f0;
}
.ms-grid i:nth-child(4) {
  background: #ffba08;
}
.login-divider {
  position: relative;
  margin: 10px 0 3px;
  border-top: 1px solid var(--rule);
  text-align: center;
}
.login-divider span {
  position: relative;
  top: -10px;
  padding: 0 9px;
  background: white;
  color: var(--text-faint);
  font-size: 11px;
}
.password-field {
  position: relative;
}
.password-field .input {
  padding-right: 44px;
}
.password-toggle {
  position: absolute;
  top: 1px;
  right: 1px;
  width: 44px;
  height: calc(100% - 2px);
  display: grid;
  place-items: center;
  border-radius: 0 5px 5px 0;
  color: var(--text-soft);
  background: transparent;
  cursor: pointer;
}
.password-toggle:hover {
  color: var(--text);
  background: var(--sheet-strong);
}
.login-submit {
  min-height: 44px;
}
.forgot-action {
  justify-self: center;
  min-height: 36px;
  padding: 7px 8px;
  color: var(--text-soft);
  background: transparent;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.reset-copy {
  margin-top: 6px;
  color: var(--text-soft);
  font-size: 13px;
}
.reset-result,
.login-notice,
.login-error {
  padding: 11px 12px;
  border-radius: 6px;
}
.reset-result {
  color: #315729;
  background: var(--success-soft);
}
.login-notice {
  color: var(--text-soft);
  background: var(--shared-soft);
}
.login-error {
  color: #7d2e2e;
  background: var(--danger-soft);
}
.demo-profile > span:nth-child(2) {
  min-width: 0;
  display: grid;
}
.demo-profile small {
  color: var(--text-soft);
}
.login-help {
  color: var(--text-soft);
  font-size: 12px;
  text-align: center;
}
@media (pointer: coarse) {
  .forgot-action {
    min-height: 44px;
  }
}
</style>
